/**
 * Men who come by road (10 §2, missions 23 and 26): the main road carried on to the north edge of an
 * open map, a walk along it cut into legs the soldiers' bounded path search can manage, and a column of
 * dormant spawns who appear at its head when their timed event fires and walk it there and back.
 *
 * Engine limits, designed around and not faked: nobody drives, and a column loops its walk for ever -
 * there is no leaving the map at the road's end.
 */

import { atan2 } from '../../sim/math';
import type { EnemyKind, PropDef, Rect, SpawnDef } from '../../sim/types';
import { NO_STAND } from '../builder';
import { FLAG } from '../grid';
import { LINES } from '../tuning';
import type { Point } from '../types';
import { clearing, hideRadius, rockHide } from './helpers3b';
import type { GoalCtx } from './types';

/** A leg this long is well inside the 6000-node search a soldier paths with (as patrols.ts). */
const MAX_LEG = 22;

/**
 * The main road, south to north. On a map whose goal has no perimeter it stops at the goal's south edge
 * (roads.ts); `through` carries it straight on to the north edge, so that traffic has somewhere to come from.
 */
export function mainRoad(g: GoalCtx, through: boolean): Point[] {
  const first = g.map.roads[0];
  const points: Point[] = (first?.points ?? []).map(([x, z]) => ({ x, z }));
  const end = points[points.length - 1] ?? { x: g.ctx.plan.roadEnd.x, z: g.ctx.plan.roadEnd.z };
  if (through) {
    g.map.road([[end.x, end.z], [end.x, 0]], LINES.roadWidth);
    points.push({ x: end.x, z: 0 });
  }
  return points;
}

/** Points along a polyline between two z values, at most `MAX_LEG` apart, from the northern end southward. */
export function alongRoad(road: readonly Point[], northZ: number, southZ: number): Point[] {
  const out: Point[] = [];
  let last: Point | null = null;
  // Walk it north to south, a metre at a time: simple, and exact enough for nodes that are resolved to cells anyway.
  for (let i = road.length - 1; i > 0; i--) {
    const a = road[i];
    const b = road[i - 1];
    if (!a || !b) continue;
    const length = Math.sqrt((b.x - a.x) * (b.x - a.x) + (b.z - a.z) * (b.z - a.z));
    const steps = Math.max(1, Math.ceil(length));
    for (let s = 0; s <= steps; s++) {
      const p = { x: a.x + ((b.x - a.x) * s) / steps, z: a.z + ((b.z - a.z) * s) / steps };
      if (p.z < northZ || p.z > southZ) continue;
      const gap: number = last ? Math.sqrt((p.x - last.x) * (p.x - last.x) + (p.z - last.z) * (p.z - last.z)) : Infinity;
      if (gap < MAX_LEG) continue;
      out.push(p);
      last = p;
    }
  }
  return out;
}

export interface ColumnMan {
  kind: EnemyKind;
  extra?: Partial<SpawnDef>;
}

/**
 * A file of dormant men who appear at `route[0]` with the timed event tagged `wakeTag` and walk the route
 * there and back. Every node is resolved to a free cell, reserved, and the legs kept clear of mines and
 * scatter. Returns the spawns, or null (and marks the attempt) when the route cannot be stood on.
 */
export function column(g: GoalCtx, route: readonly Point[], men: readonly ColumnMan[], wakeTag: string): SpawnDef[] | null {
  if (route.length < 2) {
    g.map.problems.push(`${g.ctx.spec.id}: no road for the column to walk`);
    return null;
  }
  const out: SpawnDef[] = [];
  const heading = Math.sign((route[1]?.z ?? 0) - (route[0]?.z ?? 0)) || 1;
  for (let m = 0; m < men.length; m++) {
    const man = men[m];
    if (!man) continue;
    // Two abreast on the crown of the road, each pair a couple of metres behind the last.
    const dx = m === 0 ? 0 : m % 2 === 0 ? -0.9 : 0.9;
    const dz = -heading * Math.ceil(m / 2) * 2;
    const nodes: Array<[number, number]> = [];
    for (const p of route) {
      const spot = g.map.nearestOpen(p.x + dx, p.z + dz, 3, NO_STAND);
      if (!spot) {
        g.map.problems.push(`${g.ctx.spec.id}: the road is blocked at ${Math.round(p.x)}, ${Math.round(p.z)}`);
        return null;
      }
      nodes.push(spot);
    }
    // There and back: the same nodes home again, so the loop's closing leg is as short as any other.
    const patrol = [...nodes];
    for (let i = nodes.length - 2; i >= 1; i--) {
      const node = nodes[i];
      if (node) patrol.push(node);
    }
    const [a, b] = patrol;
    if (!a || !b) continue;
    out.push(g.map.spawn({
      kind: man.kind, x: a[0], z: a[1], facing: atan2(b[0] - a[0], b[1] - a[1]), patrol,
      dormantUntil: 'timedEvent', wakeTag, ...man.extra,
    }));
    // Every man's nodes stay standable whatever the budget buys afterwards; the lead's legs stay clear of mines.
    for (const [x, z] of nodes) g.map.mask.circle(x, z, 1.2, FLAG.keep);
    if (m === 0) g.map.mask.line(nodes, 1.5, FLAG.path);
  }
  return out;
}

export interface RoadsideHide {
  zone: Rect;
  marker: PropDef;
  /** The point of the road the hide looks at. */
  roadAt: Point;
}

/**
 * A hide among the rocks beside the road where it runs through the goal's ground: far enough off the verge
 * that a file on the road passes it by (12-16 m), near enough to count heads. The marker is a walk-through
 * `overlookMarker`; it is NOT flagged here (see `flagTargets`). Null - and the attempt marked - with no room.
 */
export function roadsideHide(g: GoalCtx): RoadsideHide | null {
  const roadX = g.ctx.plan.roadEnd.x;
  const r = g.rect;
  // Whichever side of the road has more of the goal's ground on it.
  const side = roadX - r.x0 >= r.x1 - roadX ? -1 : 1;
  const x0 = roadX + side * (11 + hideRadius);
  const x1 = roadX + side * (6 + hideRadius);
  const strip: Rect = { x0: Math.min(x0, x1) - hideRadius - 1, x1: Math.max(x0, x1) + hideRadius + 1, z0: r.z0 + 3, z1: r.z1 - 1 };
  const at = clearing(g, { within: strip, radius: hideRadius });
  if (!at) {
    g.map.problems.push(`${g.ctx.spec.id}: no room for a hide beside the road`);
    return null;
  }
  const roadAt = { x: roadX, z: at.z };
  const zone = rockHide(g, at, roadAt);
  return { zone, marker: g.map.add('overlookMarker', at.x, at.z), roadAt };
}
